import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Select, Upload, Button, Table, Tag, notification, Alert } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import { imeiValidation, macValidation } from "../../utils/formValidation.jsx";
import { parseModemWorkbook } from "../../utils/modemImport.js";
import { useGetModemTypesLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

const STATUS = {
  OK: 'ok',
  DUPLICATED: 'duplicated',
  ERROR: 'error',
};

/**
 * Importacion masiva de modems desde un packing list de Teltonika.
 * La importacion debe ser de un solo tipo por cada vez
 */
function ModemImportModal({ lang, open, onClose, onImported }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false, importing: false });

  const [data, setData] = useState({
    rows: [],
    existingImeis: [],
    fileName: '',
    parseError: null,
  });

  const [typeUuid, setTypeUuid] = useState('');

  // ===== 2. SELECTORES =====
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();

  // ===== 3. DERIVADOS =====
  const typeOptions = useMemo(
    () => modemTypes.map(type => ({ value: type.uuid, label: type.code })),
    [modemTypes]
  );

  const selectedType = useMemo(
    () => modemTypes.find(type => type.uuid === typeUuid) ?? null,
    [modemTypes, typeUuid]
  );

  // Validamos en funcion del tipo elegido (cada tipo tiene requerimientos de campos particulares)
  const validatedRows = useMemo(() => {
    if (!selectedType) return data.rows.map(row => ({ ...row, status: null, message: '' }));

    const requires = selectedType.requires ?? {};
    const known = new Set(data.existingImeis);
    const seen = new Set();

    return data.rows.map(row => {
      let status = STATUS.OK;
      let message = '';

      if (!imeiValidation(row.imei)) {
        status = STATUS.ERROR;
        message = lang.handle('IMEI no válido');
      } else if (seen.has(row.imei)) {
        status = STATUS.ERROR;
        message = lang.handle('Repetido en el fichero');
      } else if (known.has(row.imei)) {
        status = STATUS.DUPLICATED;
        message = lang.handle('Ya existe en el sistema');
      } else if (requires.serial && !row.serial_number) {
        status = STATUS.ERROR;
        message = lang.handle('Falta el número de serie');
      } else if (requires.mac && !row.mac) {
        status = STATUS.ERROR;
        message = lang.handle('Falta la MAC');
      } else if (row.mac && !macValidation(row.mac)) {
        status = STATUS.ERROR;
        message = lang.handle('MAC no válida');
      } else if (requires.password && !row.device_password) {
        status = STATUS.ERROR;
        message = lang.handle('Falta la contraseña');
      }

      seen.add(row.imei);
      return { ...row, status, message };
    });
  }, [data.rows, data.existingImeis, selectedType, lang]);

  const summary = useMemo(() => ({
    total: validatedRows.length,
    ok: validatedRows.filter(row => row.status === STATUS.OK).length,
    duplicated: validatedRows.filter(row => row.status === STATUS.DUPLICATED).length,
    error: validatedRows.filter(row => row.status === STATUS.ERROR).length,
  }), [validatedRows]);

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (payload) => setData(prev => ({ ...prev, ...payload }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  
  const fetchExistingImeis = useCallback(async () => {
    updateUI('loading', true);
    try {
      const res = await service.get("modems");
      if (!res || res.data.ok === false) return;
      updateData({ existingImeis: (res.data.data || []).map(modem => modem.imei) });
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  }, []);

  // ===== 6. HANDLERS =====
  const reset = () => {
    setData({ rows: [], existingImeis: data.existingImeis, fileName: '', parseError: null });
    setTypeUuid('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file) => {
    updateUI('loading', true);
    try {
      const { rows, error } = await parseModemWorkbook(file);
      updateData({ rows, parseError: error, fileName: file.name });
    } catch (err) {
      console.error("Error al leer el fichero: " + err);
      updateData({ rows: [], parseError: lang.handle('No se ha podido leer el fichero'), fileName: file.name });
    } finally {
      updateUI('loading', false);
    }
    return false;
  };

  const handleImport = async () => {
    const modems = validatedRows
      .filter(row => row.status === STATUS.OK)
      .map(row => ({
        imei: row.imei,
        modem_type_uuid: typeUuid,
        serial_number: row.serial_number || null,
        mac: row.mac || null,
        device_password: row.device_password || null,
      }));

    updateUI('importing', true);
    try {
      const res = await service.post("modems/import", { modems });

      if (!res || res.data.ok === false) {
        notification["error"]({ message: lang.handle(res?.data?.message) });
        return;
      }

      const result = res.data.data ?? {};
      notification["success"]({
        message: `${lang.handle('Modems importados')}: ${result.inserted ?? 0}`,
      });

      if (onImported) onImported();
      handleClose();
    } catch (err) {
      console.error("Error devuelto: " + err);
      notification["error"]({ message: lang.handle('Error en la operación') });
    } finally {
      updateUI('importing', false);
    }
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    if (open) fetchExistingImeis();
  }, [open, fetchExistingImeis]);

  // ===== 8. CONFIG =====
  const columns = [
    {
      title: lang.handle('Fila'),
      dataIndex: 'excel_row',
      width: 70,
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'status',
      width: 130,
      render: (status) => {
        if (status === STATUS.OK) return <Tag color="green">{lang.handle('Válido')}</Tag>;
        if (status === STATUS.DUPLICATED) return <Tag color="orange">{lang.handle('Duplicado')}</Tag>;
        if (status === STATUS.ERROR) return <Tag color="red">{lang.handle('Error')}</Tag>;
        return <Tag>{lang.handle('Sin tipo')}</Tag>;
      },
    },
    { title: 'IMEI', dataIndex: 'imei' },
    { title: lang.handle('Número de serie'), dataIndex: 'serial_number' },
    { title: 'MAC', dataIndex: 'mac' },
    { title: lang.handle('Contraseña'), dataIndex: 'device_password' },
    { title: lang.handle('Observaciones'), dataIndex: 'message' },
  ];

  // ===== 9. RENDER =====
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={lang.handle('Importar modems')}
      width={1000}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {lang.handle('Cancelar')}
        </Button>,
        <Button
          key="import"
          type="primary"
          loading={ui.importing}
          disabled={summary.ok === 0}
          onClick={handleImport}
        >
          {`${lang.handle('Importar')} ${summary.ok}`}
        </Button>,
      ]}
    >
      <div className="form-group row">
        <div className="col-sm-3 newCard-form-text"> {lang.handle('Tipo Teltonika')} </div>
        <div className="col-sm-9">
          <Select
            className="form-control"
            placeholder={lang.handle('Seleccionar opción')}
            value={typeUuid || undefined}
            onChange={setTypeUuid}
            options={typeOptions}
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-group row">
        <div className="col-sm-3 newCard-form-text"> {lang.handle('Fichero')} </div>
        <div className="col-sm-9">
          <Upload
            beforeUpload={handleFile}
            maxCount={1}
            accept=".xlsx,.xls"
            showUploadList={false}
            disabled={!typeUuid}
          >
            <Button icon={<UploadOutlined />} disabled={!typeUuid} loading={ui.loading}>
              {lang.handle('Seleccionar fichero')}
            </Button>
          </Upload>
          {data.fileName && <span style={{ marginLeft: 12 }}>{data.fileName}</span>}
        </div>
      </div>

      {data.parseError && (
        <Alert type="error" showIcon message={data.parseError} style={{ marginBottom: 16 }} />
      )}

      {validatedRows.length > 0 && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              `${summary.total} ${lang.handle('filas leídas')} · ` +
              `${summary.ok} ${lang.handle('válidas')} · ` +
              `${summary.duplicated} ${lang.handle('duplicadas')} · ` +
              `${summary.error} ${lang.handle('con error')}`
            }
          />
          <Table
            columns={columns}
            dataSource={validatedRows}
            rowKey="excel_row"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        </>
      )}
    </Modal>
  );
}

export default withLocale(ModemImportModal);
